import React, { useRef, useState, useCallback } from 'react';
import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { AnchoredModalProps, anchoredModalRadiusClassNameMapping } from './Types';

function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

const AnchoredModal = React.forwardRef<HTMLDivElement, AnchoredModalProps>(function AnchoredModal(
  {
    className,
    children,
    placement = 'bottom',
    offset: offsetValue = 12,
    showArrow = true,
    arrowSize = 16,
    closeOnEscape = true,
    closeOnOutsideClick = true,
    closeOnContentClick = true,
    returnFocus = true,
    radius = 'lg',
    renderTrigger,
    isOpen: controlledIsOpen,
    onOpenChange,
    onClose,
    onOpen,
    ...others
  },
  ref,
) {
  const arrowRef = useRef<SVGSVGElement>(null);
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isControlled) {
        onOpenChange?.(open);
      } else {
        setUncontrolledIsOpen(open);
      }
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [isControlled, onOpenChange, onOpen, onClose],
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const middleware = [
    offset(offsetValue),
    flip(),
    shift({ padding: 8 }),
    ...(showArrow ? [arrow({ element: arrowRef })] : []),
  ];

  const { refs, floatingStyles, context } = useFloating<HTMLElement>({
    placement,
    open: isOpen,
    middleware,
    whileElementsMounted: autoUpdate,
    onOpenChange: handleOpenChange,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      escapeKey: closeOnEscape,
      outsidePress: (event) => {
        if (!closeOnOutsideClick) return false;

        const floatingEl = refs.floating.current;
        const target = event.target as Node;
        const targetEl = target as HTMLElement;
        const isInsideFloating = floatingEl?.contains(target);
        const closestModal = targetEl.closest?.('[data-testid="execonline-anchored-modal"]');

        if (!closeOnContentClick && (isInsideFloating || closestModal)) {
          return false;
        }

        return true;
      },
    }),
    useRole(context, { role: 'dialog' }),
  ]);

  const handleElementRef = useMergeRefs([ref, refs.setFloating]);

  return (
    <>
      {renderTrigger?.(refs.setReference, getReferenceProps)}

      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay className="z-50 bg-foreground/40" lockScroll />
          <FloatingFocusManager
            context={context}
            initialFocus={refs.floating}
            returnFocus={returnFocus}
          >
            <div
              data-testid="execonline-anchored-modal"
              aria-modal="true"
              ref={handleElementRef}
              style={floatingStyles}
              className={cn(
                'border border-solid border-border bg-card',
                'shadow-popover',
                'px-3.5 py-5',
                'z-[51]',
                anchoredModalRadiusClassNameMapping[radius],
                className,
              )}
              {...getFloatingProps()}
              {...others}
            >
              {showArrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  fill="var(--card)"
                  stroke="var(--border)"
                  strokeWidth={1}
                  width={arrowSize}
                  height={arrowSize / 2}
                />
              )}
              {typeof children === 'function' ? children({ onClose: handleClose }) : children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
});

AnchoredModal.displayName = 'AnchoredModal';

export default AnchoredModal;
export { AnchoredModal };
export type { AnchoredModalProps, AnchoredModalRadius } from './Types';
